/**
 * 🛡️ Error Interceptor Service
 * 
 * Captura TODOS os tipos de erros do frontend:
 * 1. Erros de API/Axios (HTTP errors)
 * 2. Erros globais de JavaScript (window.onerror)
 * 3. Promise rejections não tratadas (unhandledrejection)
 * 4. Erros Vue (via app.config.errorHandler)
 * 5. Console errors (opcional, para debug)
 * 
 * Envia automaticamente para o Oráculo Logs
 */

import oraculoLogs from './oraculo-logs.service';
import axios from 'axios';

class ErrorInterceptorService {
  constructor() {
    this.userId = null;
    this.router = null;
    this.store = null;
    this.initialized = false;
    
    // 🛡️ SISTEMA DE DEDUPLICAÇÃO
    // Usa WeakMap para rastrear erros já logados (não causa memory leak)
    this.loggedErrors = new WeakMap();
    this.DEDUP_WINDOW = 5000; // 5 segundos
    
    // ⚠️ DESABILITADO: try/catch nos services já captura HTTP errors
    // this.setupGlobalAxiosInterceptor();
  }

  /**
   * 🎯 Registrar interceptor no axios GLOBAL
   * Captura TODOS os erros HTTP, independente de usar 'api' ou 'axios' direto
   */
  setupGlobalAxiosInterceptor() {
    axios.interceptors.response.use(
      // Sucesso: retorna resposta normalmente
      (response) => response,
      
      // Erro: captura e envia para Oráculo
      (error) => {
        // 🛡️ GUARD: Ignora erros do próprio Oráculo para evitar loop infinito
        if (error.config?.url && error.config.url.includes('/api/logs/ingest')) {
          console.warn('⚠️ [Axios Global Interceptor] Erro do Oráculo - IGNORANDO para evitar loop');
          return Promise.reject(error);
        }

        console.log('🔥 [Axios Global Interceptor] Erro detectado:', {
          isAxiosError: error.isAxiosError,
          code: error.code,
          message: error.message,
          url: error.config?.url
        });
        
        // Capturar erro HTTP com logs enriquecidos
        this.handleHttpError(error);
        
        // Se for erro de autenticação (401/403), logar especificamente
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.handleAuthError(error);
        }
        
        // Rejeitar a promise para que o erro continue propagando
        return Promise.reject(error);
      }
    );
    
    console.log('✅ Axios Global Interceptor registrado');
  }

  /**
   * Inicializar interceptor com contexto da aplicação
   * @param {Object} options - { router, store }
   */
  initialize({ router, store }) {
    if (this.initialized) {
      console.warn('⚠️ ErrorInterceptor já foi inicializado');
      return;
    }

    this.router = router;
    this.store = store;
    this.initialized = true;

    // Setup de todos os interceptors
    this.setupWindowErrorHandler();
    this.setupUnhandledRejectionHandler();
    this.setupConsoleErrorInterceptor();

    console.log('✅ ErrorInterceptor inicializado com sucesso');
  }

  /**
   * Atualizar userId dinamicamente (chamar após login/logout)
   */
  setUserId(userId) {
    this.userId = userId;
  }

  /**
   * Obter userId do store se disponível
   */
  getUserId() {
    return this.userId || this.store?.state?.auth?.userId || null;
  }

  /**
   * Obter contexto atual da aplicação
   */
  getAppContext() {
    const route = this.router?.currentRoute?.value;
    
    return {
      route: route?.path || window.location.pathname,
      routeName: route?.name || 'unknown',
      query: route?.query ? JSON.stringify(route.query) : null,
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      online: navigator.onLine,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * 🛡️ SISTEMA DE DEDUPLICAÇÃO
   * Verifica se erro já foi logado recentemente (últimos 5s)
   * @param {Error} error - Objeto de erro
   * @returns {boolean} - true se pode logar, false se é duplicado
   */
  shouldLogError(error) {
    // Se não é objeto Error, sempre logar (primitivos não podem ser rastreados)
    if (!error || typeof error !== 'object') {
      return true;
    }

    const now = Date.now();
    const lastLogged = this.loggedErrors.get(error);

    // Se nunca foi logado, ou foi logado há mais de 5s, pode logar
    if (!lastLogged || (now - lastLogged) > this.DEDUP_WINDOW) {
      this.loggedErrors.set(error, now);
      return true;
    }

    // Já foi logado recentemente - BLOQUEAR
    console.log('⚠️ [Deduplicação] Erro já foi logado há', (now - lastLogged), 'ms - Ignorando');
    return false;
  }

  /**
   * 1️⃣ Interceptar erros globais de JavaScript
   * Captura erros como: ReferenceError, TypeError, SyntaxError, etc.
   */
  setupWindowErrorHandler() {
    window.addEventListener('error', (event) => {
      // Evitar duplicação de logs se o erro for tratado
      if (event.defaultPrevented) return;

      const error = event.error || {};
      
      // 🛡️ DEDUPLICAÇÃO: Verificar se já foi logado
      if (error && !this.shouldLogError(error)) {
        console.log('⚠️ [window.onerror] Erro já foi logado recentemente - IGNORANDO');
        return;
      }
      
      // Capturar TODOS os detalhes do erro
      const errorDetails = {
        type: error.name || 'Error',
        message: event.message || error.message || 'Unknown error',
        filename: event.filename || 'unknown',
        lineno: event.lineno || 0,
        colno: event.colno || 0,
        stack: error.stack || 'No stack trace',
        // Capturar propriedades extras do erro se existirem
        ...(error.code && { code: error.code }),
        ...(error.cause && { cause: String(error.cause) })
      };

      console.error('🔴 [Window Error]', error, errorDetails);

      // Enviar para Oráculo com mensagem do erro como título principal
      const mainMessage = errorDetails.message || 'Global JavaScript Error';
      
      oraculoLogs.error(mainMessage, {
        userId: this.getUserId(),
        details: {
          // ✅ Todos os campos como primitivos
          detectedBy: 'window.onerror', // ✅ Indica onde foi detectado
          errorType: errorDetails.type,
          errorStack: errorDetails.stack,
          errorCode: errorDetails.code,
          errorCause: errorDetails.cause ? String(errorDetails.cause) : null,
          filename: errorDetails.filename,
          lineno: errorDetails.lineno,
          colno: errorDetails.colno,
          ...this.getAppContext(),
          category: 'frontend-error',
          source: 'window.onerror'
        }
      });
    });

    console.log('✅ Window error handler configurado');
  }

  /**
   * 2️⃣ Interceptar Promise rejections não tratadas
   * Captura promises que rejeitaram sem .catch()
   */
  setupUnhandledRejectionHandler() {
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      
      // 🛡️ FILTRO: IGNORAR AXIOSERROR (Axios interceptor vai capturar)
      if (reason && typeof reason === 'object' && 
          (reason.isAxiosError === true || reason.name === 'AxiosError')) {
        console.log('⚠️ [unhandledrejection] AxiosError detectado - IGNORANDO (Axios interceptor vai capturar)');
        return;
      }
      
      // 🛡️ DEDUPLICAÇÃO: Verificar se já foi logado
      if (reason instanceof Error && !this.shouldLogError(reason)) {
        console.log('⚠️ [unhandledrejection] Erro já foi logado recentemente - IGNORANDO');
        return;
      }
      
      // Processar reason corretamente (pode ser Error, string, ou objeto)
      let errorDetails;
      
      if (reason instanceof Error) {
        // É um objeto Error - capturar tudo
        errorDetails = {
          type: reason.name || 'UnhandledRejection',
          message: reason.message || 'Promise rejected',
          stack: reason.stack || 'No stack trace',
          code: reason.code || null,
          
          // ✅ Capturar Axios config completo (se for AxiosError)
          isAxiosError: reason.isAxiosError || false,
          ...(reason.config && { 
            requestConfig: {
              method: reason.config.method,
              url: reason.config.url,
              baseURL: reason.config.baseURL,
              data: reason.config.data,
              params: reason.config.params,
              headers: reason.config.headers,
              timeout: reason.config.timeout,
              withCredentials: reason.config.withCredentials
            }
          }),
          ...(reason.response && { 
            response: {
              status: reason.response.status,
              statusText: reason.response.statusText,
              data: reason.response.data,
              headers: reason.response.headers
            }
          })
        };
      } else if (typeof reason === 'object' && reason !== null) {
        // É um objeto mas não Error
        try {
          errorDetails = {
            type: 'UnhandledRejection',
            message: reason.message || JSON.stringify(reason),
            rawReason: JSON.stringify(reason, null, 2)
          };
        } catch (e) {
          errorDetails = {
            type: 'UnhandledRejection',
            message: String(reason),
            rawReason: '[Object não serializável]'
          };
        }
      } else {
        // É string ou primitivo
        errorDetails = {
          type: 'UnhandledRejection',
          message: String(reason),
          rawReason: String(reason)
        };
      }

      console.error('🔴 [Unhandled Promise Rejection]', reason, errorDetails);

      // Enviar para Oráculo com mensagem do erro como título principal
      const mainMessage = errorDetails.message || 'Unhandled Promise Rejection';
      
      oraculoLogs.error(mainMessage, {
        userId: this.getUserId(),
        details: {
          // ✅ Identificação
          detectedBy: 'unhandled-rejection',
          errorType: errorDetails.type,
          errorStack: errorDetails.stack || 'No stack',
          errorCode: errorDetails.code || null,
          isAxiosError: errorDetails.isAxiosError || false,
          
          // ✅ Request completo (se for Axios)
          ...(errorDetails.requestConfig && {
            requestUrl: errorDetails.requestConfig.baseURL 
              ? `${errorDetails.requestConfig.baseURL}${errorDetails.requestConfig.url}`
              : errorDetails.requestConfig.url,
            requestMethod: errorDetails.requestConfig.method?.toUpperCase(),
            requestData: JSON.stringify(errorDetails.requestConfig.data || {}),
            requestParams: JSON.stringify(errorDetails.requestConfig.params || {}),
            requestTimeout: errorDetails.requestConfig.timeout || 0,
            hasAuthToken: !!(errorDetails.requestConfig.headers?.Authorization || errorDetails.requestConfig.headers?.authorization),
            authTokenPreview: errorDetails.requestConfig.headers?.Authorization 
              ? errorDetails.requestConfig.headers.Authorization.substring(0, 20) + '...'
              : (errorDetails.requestConfig.headers?.authorization 
                  ? errorDetails.requestConfig.headers.authorization.substring(0, 20) + '...'
                  : null),
            withCredentials: errorDetails.requestConfig.withCredentials || false
          }),
          
          // ✅ Response completo (se houver)
          ...(errorDetails.response && {
            responseStatus: errorDetails.response.status,
            responseStatusText: errorDetails.response.statusText,
            responseData: typeof errorDetails.response.data === 'string' 
              ? errorDetails.response.data 
              : JSON.stringify(errorDetails.response.data || {}),
            responseHeaders: JSON.stringify(errorDetails.response.headers || {})
          }),
          
          rawReason: errorDetails.rawReason || null,
          ...this.getAppContext(),
          category: 'frontend-error',
          source: 'unhandledrejection'
        }
      });

      // Prevenir que o erro seja jogado no console novamente
      event.preventDefault();
    });

    console.log('✅ Unhandled rejection handler configurado');
  }

  /**
   * 3️⃣ Interceptar console.error (APENAS para erros de frontend não-HTTP)
   * ⚠️ IGNORA AxiosError - deixa o Axios interceptor capturar
   */
  setupConsoleErrorInterceptor() {
    const originalConsoleError = console.error;

    console.error = (...args) => {
      // Chamar console.error original
      originalConsoleError.apply(console, args);

      // �️ FILTRO 1: IGNORAR AXIOSERROR COMPLETAMENTE
      // Axios errors devem ser capturados APENAS pelo Axios interceptor
      const hasAxiosError = args.some(arg => 
        arg && 
        typeof arg === 'object' && 
        (arg.isAxiosError === true || arg.name === 'AxiosError')
      );

      if (hasAxiosError) {
        console.log('⚠️ [console.error] AxiosError detectado - IGNORANDO (Axios interceptor vai capturar)');
        return; // ⬅️ NÃO LOGAR - deixar Axios interceptor fazer o trabalho
      }

      // CÓDIGO ORIGINAL - Para erros NÃO-AXIOS
      let errorObject = null;
      let messageText = '';
      const processedArgs = [];

      args.forEach(arg => {
        if (arg instanceof Error) {
          // 🛡️ DEDUPLICAÇÃO: Verificar se já foi logado
          if (!this.shouldLogError(arg)) {
            return; // Já foi logado recentemente, skip
          }

          // Capturar erro completo com stack
          errorObject = {
            type: arg.name || 'Error',
            message: arg.message || 'Unknown error',
            stack: arg.stack || 'No stack trace',
            code: arg.code || null,
            ...arg // Capturar propriedades extras
          };
          processedArgs.push(`${arg.name}: ${arg.message}`);
        } else if (typeof arg === 'object' && arg !== null) {
          // Tentar serializar objeto
          try {
            const serialized = JSON.stringify(arg, null, 2);
            processedArgs.push(serialized);
          } catch (e) {
            processedArgs.push('[Object - não serializável]');
          }
        } else {
          processedArgs.push(String(arg));
        }
      });

      messageText = processedArgs.join(' ');

      // Enviar apenas se não for um log do próprio Oráculo
      if (!messageText.includes('Oráculo')) {
        // ✅ Usar mensagem do erro como message principal (mais fácil buscar)
        const mainMessage = errorObject?.message || messageText || 'Console Error';
        
        oraculoLogs.error(mainMessage, {
          userId: this.getUserId(),
          details: {
            fullMessage: messageText, // Mensagem completa com contexto
            detectedBy: 'console.error', // ✅ Indica onde foi detectado
            arguments: args.length,
            // ✅ Converter argumentTypes array para string
            argumentTypes: args.map(arg => 
              arg instanceof Error ? 'Error' : 
              Array.isArray(arg) ? 'Array' : 
              typeof arg
            ).join(', '),
            // ✅ Adicionar propriedades do erro como strings separadas
            ...(errorObject && {
              errorType: errorObject.type,
              errorStack: errorObject.stack,
              errorCode: errorObject.code
            }),
            ...this.getAppContext(),
            category: 'frontend-error',
            source: 'console.error'
          }
        });
      }
    };

    console.log('✅ Console error interceptor configurado');
  }

  /**
   * 4️⃣ Interceptor para erros Vue (usar em main.js)
   * @param {Error} error - Erro capturado
   * @param {Object} instance - Instância do componente Vue
   * @param {String} info - Informação adicional
   */
  handleVueError(error, instance, info) {
    // 🛡️ DEDUPLICAÇÃO: Verificar se já foi logado
    if (!this.shouldLogError(error)) {
      console.log('⚠️ [Vue Error] Erro já foi logado recentemente - IGNORANDO');
      return;
    }
    
    // Capturar TODOS os detalhes do erro Vue
    const errorDetails = {
      type: error.name || 'VueError',
      message: error.message || 'Unknown Vue error',
      stack: error.stack || 'No stack trace',
      code: error.code || null,
      
      // Component details
      component: instance?.$options?.name || instance?.$options?.__name || 'Anonymous Component',
      componentFile: instance?.$options?.__file || 'Unknown file',
      lifecycle: info || 'unknown lifecycle',
      
      // Props e data (se disponível)
      props: instance?.$props ? Object.keys(instance.$props) : [],
      dataKeys: instance?.$data ? Object.keys(instance.$data) : [],
      
      // Erro original completo
      originalError: {
        ...error,
        toString: error.toString ? error.toString() : String(error)
      }
    };

    console.error('🔴 [Vue Error]', error, errorDetails, instance);

    // Enviar para Oráculo com mensagem do erro como título principal
    const mainMessage = errorDetails.message || 'Vue Component Error';
    
    oraculoLogs.error(mainMessage, {
      userId: this.getUserId(),
      details: {
        // ✅ Todos os campos como primitivos
        detectedBy: 'vue-error-handler', // ✅ Indica onde foi detectado
        errorType: errorDetails.type,
        errorStack: errorDetails.stack,
        errorCode: errorDetails.code || null,
        component: errorDetails.component,
        componentFile: errorDetails.componentFile,
        lifecycle: errorDetails.lifecycle,
        // ✅ Arrays convertidos para strings
        props: errorDetails.props.join(', '),
        dataKeys: errorDetails.dataKeys.join(', '),
        vueInfo: info,
        ...this.getAppContext(),
        category: 'frontend-error',
        source: 'vue-error-handler'
      }
    });
  }

  /**
   * 5️⃣ Capturar erro HTTP (chamar do Axios interceptor)
   * @param {Object} error - Erro do Axios
   */
  handleHttpError(error) {
    // 🛡️ GUARD: Ignora erros do próprio Oráculo (primeira linha de defesa)
    if (error.config?.url && error.config.url.includes('/api/logs/ingest')) {
      console.warn('⚠️ [handleHttpError] Erro do Oráculo - IGNORANDO para evitar loop infinito');
      return;
    }

    console.log('🎯 [handleHttpError] CHAMADO - Erro Axios detectado:', {
      isAxiosError: error.isAxiosError,
      code: error.code,
      message: error.message,
      url: error.config?.url,
      method: error.config?.method
    });

    // 🛡️ DEDUPLICAÇÃO: Verificar se já foi logado
    if (!this.shouldLogError(error)) {
      console.log('⚠️ [handleHttpError] Erro já foi logado recentemente - IGNORANDO');
      return;
    }

    const request = error.config || {};
    const response = error.response || {};

    // Capturar TODOS os detalhes do erro Axios
    const errorDetails = {
      type: error.name || 'AxiosError',
      message: error.message || 'HTTP request failed',
      code: error.code || null, // ERR_NETWORK, ECONNABORTED, etc.
      method: request.method?.toUpperCase() || 'UNKNOWN',
      url: request.url || request.baseURL || 'unknown',
      fullUrl: request.baseURL ? `${request.baseURL}${request.url}` : request.url,
      status: response.status || 0,
      statusText: response.statusText || 'Unknown',
      stack: error.stack || 'No stack trace',
      
      // Request details
      requestHeaders: request.headers || {},
      requestData: request.data || null,
      requestParams: request.params || null,
      
      // Response details
      responseHeaders: response.headers || {},
      responseData: response.data || null,
      
      // Timing
      timeout: request.timeout || null,
      
      // Axios specifics
      isAxiosError: error.isAxiosError || false,
      toJSON: error.toJSON ? error.toJSON() : null
    };

    console.error('🔴 [HTTP Error]', error, errorDetails);

    // Enviar para Oráculo com estrutura COMPLETA e RICA
    oraculoLogs.logHttpError({
      method: errorDetails.method,
      path: errorDetails.fullUrl,
      status: errorDetails.status,
      userId: this.getUserId(),
      details: {
        // ✅ Identificação do erro
        errorType: errorDetails.type,
        errorCode: errorDetails.code,
        errorMessage: errorDetails.message,
        errorStack: errorDetails.stack,
        
        // ✅ Request completo
        requestUrl: errorDetails.fullUrl,
        requestMethod: errorDetails.method,
        requestData: typeof errorDetails.requestData === 'string' 
          ? errorDetails.requestData 
          : JSON.stringify(errorDetails.requestData || {}),
        requestParams: JSON.stringify(errorDetails.requestParams || {}),
        requestTimeout: errorDetails.timeout || 0,
        
        // ✅ Request Headers (CRÍTICO para debug de auth)
        hasAuthToken: !!(errorDetails.requestHeaders?.Authorization || errorDetails.requestHeaders?.authorization),
        authTokenPreview: errorDetails.requestHeaders?.Authorization 
          ? errorDetails.requestHeaders.Authorization.substring(0, 20) + '...'
          : (errorDetails.requestHeaders?.authorization 
              ? errorDetails.requestHeaders.authorization.substring(0, 20) + '...'
              : null),
        contentType: errorDetails.requestHeaders?.['Content-Type'] || errorDetails.requestHeaders?.['content-type'] || null,
        withCredentials: request.withCredentials || false,
        
        // ✅ Response completo
        responseStatus: errorDetails.status,
        responseStatusText: errorDetails.statusText,
        responseData: typeof errorDetails.responseData === 'string'
          ? errorDetails.responseData
          : JSON.stringify(errorDetails.responseData || {}),
        responseHeaders: JSON.stringify(errorDetails.responseHeaders || {}),
        
        // ✅ Axios specifics
        isAxiosError: errorDetails.isAxiosError,
        
        // ✅ Contexto da aplicação
        ...this.getAppContext(),
        category: 'api-error',
        source: 'axios-interceptor',
        detectedBy: 'axios-interceptor'
      }
    });
  }

  /**
   * 6️⃣ Capturar erro de autenticação (401/403)
   * @param {Object} error - Erro HTTP de autenticação
   */
  handleAuthError(error) {
    // 🛡️ DEDUPLICAÇÃO: Verificar se já foi logado
    if (!this.shouldLogError(error)) {
      console.log('⚠️ [Auth Error] Erro já foi logado recentemente - IGNORANDO');
      return;
    }
    
    const response = error.response || {};
    
    console.error('🔴 [Auth Error]', {
      status: response.status,
      message: response.data?.message || 'Authentication failed'
    });

    oraculoLogs.logAuth({
      action: 'auth_error',
      userId: this.getUserId(),
      success: false,
      error: error,
      details: {
        status: response.status,
        url: error.config?.url,
        ...this.getAppContext(),
        category: 'auth-error'
      }
    });
  }

  /**
   * 7️⃣ Log manual de erro (para try/catch específicos)
   * @param {String} message - Mensagem descritiva
   * @param {Error} error - Objeto de erro
   * @param {Object} context - Contexto adicional
   */
  logError(message, error, context = {}) {
    console.error(`🔴 [Manual Error] ${message}`, error);

    oraculoLogs.error(message, {
      userId: this.getUserId(),
      error: error ? oraculoLogs.captureErrorDetails(error) : null,
      details: {
        ...this.getAppContext(),
        ...context,
        category: context.category || 'manual-error'
      }
    });
  }
}

export default new ErrorInterceptorService();
